import React, { useState } from 'react';
import { Brain, Target, Award, CheckCircle, ArrowRight, BarChart, Star } from 'lucide-react';

type Skill = {
  id: string;
  name: string;
  category: string;
  level: number;
  progress: number;
  description: string;
};

type Assessment = {
  id: string;
  title: string;
  description: string;
  duration: string;
  difficulty: 'fácil' | 'intermedio' | 'avanzado';
  skills: string[];
  xpReward: number;
};

const AISkillAssessment = () => {
  const [selectedSkill, setSelectedSkill] = useState<string | null>(null);
  const [showAssessment, setShowAssessment] = useState(false);

  const skills: Skill[] = [
    {
      id: 'skill-1',
      name: 'Definición de Producto',
      category: 'Product Management',
      level: 3,
      progress: 75,
      description: 'Capacidad para definir y articular claramente la visión y estrategia del producto.'
    },
    {
      id: 'skill-2',
      name: 'Investigación de Usuario',
      category: 'Product Management',
      level: 2,
      progress: 45,
      description: 'Habilidad para conducir investigaciones efectivas de usuarios y analizar resultados.'
    },
    {
      id: 'skill-3',
      name: 'Priorización',
      category: 'Product Management',
      level: 4,
      progress: 85,
      description: 'Capacidad para priorizar características y decisiones de producto basadas en datos.'
    }
  ];

  const assessments: Assessment[] = [
    {
      id: 'assessment-1',
      title: 'Evaluación de Definición de Producto',
      description: 'Demuestra tu capacidad para definir un producto digital efectivo',
      duration: '30 min',
      difficulty: 'intermedio',
      skills: ['Definición de Producto', 'Estrategia'],
      xpReward: 100
    },
    {
      id: 'assessment-2',
      title: 'Prueba de Investigación de Usuario',
      description: 'Evalúa tus habilidades de investigación y análisis de usuarios',
      duration: '45 min',
      difficulty: 'avanzado',
      skills: ['Investigación de Usuario', 'Análisis'],
      xpReward: 150
    }
  ];

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'fácil':
        return 'text-green-500';
      case 'intermedio':
        return 'text-yellow-500';
      case 'avanzado':
        return 'text-red-500';
      default:
        return 'text-gray-500';
    }
  };

  const getProgressColor = (progress: number) => {
    if (progress >= 75) return 'bg-green-500';
    if (progress >= 50) return 'bg-yellow-500';
    return 'bg-orange-500';
  };

  return (
    <div className="bg-white rounded-lg shadow-sm">
      <div className="p-6 border-b">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">Evaluación de Habilidades</h2>
          <div className="flex items-center space-x-4">
            <div className="flex items-center">
              <Target className="h-5 w-5 text-accent mr-1" />
              <span className="text-sm font-medium text-gray-900">Nivel Actual: Intermedio</span>
            </div>
            <div className="flex items-center">
              <Award className="h-5 w-5 text-yellow-500 mr-1" />
              <span className="text-sm font-medium text-gray-900">850 XP</span>
            </div>
          </div>
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>Progreso hacia el siguiente nivel</span>
            <span>75%</span>
          </div>
          <div className="mt-2 h-2 bg-gray-200 rounded-full">
            <div
              className="h-2 bg-accent rounded-full transition-all duration-300"
              style={{ width: '75%' }}
            ></div>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Habilidades Actuales</h3>
            <div className="space-y-4">
              {skills.map((skill) => (
                <div
                  key={skill.id}
                  className={`border rounded-lg p-4 transition-all duration-300 ${
                    selectedSkill === skill.id
                      ? 'border-accent bg-accent/5'
                      : 'hover:border-accent/50 hover:shadow-sm'
                  }`}
                  onClick={() => setSelectedSkill(skill.id)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">{skill.name}</h4>
                      <p className="text-xs text-gray-500">{skill.category}</p>
                    </div>
                    <div className="flex items-center">
                      {[...Array(5)].map((_, index) => (
                        <Star
                          key={index}
                          className={`h-4 w-4 ${
                            index < skill.level ? 'text-yellow-500 fill-current' : 'text-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                  </div>

                  <div className="mt-2">
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>Progreso</span>
                      <span>{skill.progress}%</span>
                    </div>
                    <div className="mt-1 h-1 bg-gray-200 rounded-full">
                      <div
                        className={`h-1 rounded-full ${getProgressColor(skill.progress)}`}
                        style={{ width: `${skill.progress}%` }}
                      ></div>
                    </div>
                  </div>

                  <p className="mt-2 text-sm text-gray-600">{skill.description}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Evaluaciones Disponibles</h3>
            <div className="space-y-4">
              {assessments.map((assessment) => (
                <div
                  key={assessment.id}
                  className="border rounded-lg p-4 hover:border-accent/50 hover:shadow-sm transition-all duration-300"
                >
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium text-gray-900">{assessment.title}</h4>
                    <span
                      className={`text-xs font-medium ${getDifficultyColor(assessment.difficulty)}`}
                    >
                      {assessment.difficulty}
                    </span>
                  </div>

                  <p className="mt-1 text-sm text-gray-600">{assessment.description}</p>

                  <div className="mt-4 flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <span className="text-xs text-gray-500">{assessment.duration}</span>
                      <div className="flex items-center">
                        <Award className="h-4 w-4 text-yellow-500 mr-1" />
                        <span className="text-xs text-gray-500">+{assessment.xpReward} XP</span>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowAssessment(true)}
                      className="text-accent hover:text-accent/90 text-sm font-medium flex items-center"
                    >
                      Comenzar evaluación
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </button>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    {assessment.skills.map((skill) => (
                      <span
                        key={skill}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent/10 text-accent"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-8 bg-accent/5 rounded-lg p-6">
          <div className="flex items-start">
            <Brain className="h-6 w-6 text-accent flex-shrink-0" />
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900">Recomendaciones de la IA</h3>
              <p className="mt-1 text-gray-600">
                Basado en tu perfil y progreso actual, te recomendamos:
              </p>
              <ul className="mt-4 space-y-2">
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                  <span className="text-gray-700">
                    Completar la evaluación de Investigación de Usuario para mejorar tu nivel actual
                  </span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                  <span className="text-gray-700">
                    Practicar más ejercicios de priorización para alcanzar el siguiente nivel
                  </span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                  <span className="text-gray-700">
                    Revisar los recursos adicionales sobre Definición de Producto
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AISkillAssessment;